import { useEffect, useRef, useState } from 'react'

type ReturnType = {
  isVisible: boolean
  ref: React.RefObject<HTMLElement>
}

/**
 * This hook is used to determine if an element is visible in the viewport.
 * It uses the Intersection Observer API to observe the element and updates the state accordingly.
 */
const useIsElementVisible = (): ReturnType => {
  const [isVisible, setIsVisible] = useState(true)
  const ref = useRef<HTMLElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting)
      },
      {
        root: null,
        threshold: 0,
      }
    )

    const current = ref.current
    if (current) observer.observe(current)

    return () => {
      if (current) observer.unobserve(current)
    }
    // Note: it should not be necessary to add this ref. However, without it the visibility status does not update correctly all the time. Thus its necessary.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref.current])

  return { isVisible, ref }
}

export default useIsElementVisible
